import React, { useState } from 'react';
import CardTopbar from './CardTopbar';
import './CompactCardTile.css';

/* What the tile carries besides its art. Both off: the hover panel and the tap
   overlay already say everything the info line and the name said, and without
   them the grid reads as the wall of icons the compact view is for. Flip either
   back on to get it — ItemCard and SetCard still pass both, so nothing else has
   to change. The equip button is deliberately not part of the switch: it is an
   action rather than a label, and it is the one thing the panel cannot offer. */
const SHOW_TOPBAR_INFO = false;
const SHOW_NAME = false;
/* The elemental halo the detailed card puts behind its artwork. On: it is the
   only shadow the tile has — the plain black drop shadow it used to fall back
   to is gone, so an icon with no element reads flat rather than lifted. */
const SHOW_ELEMENT_GLOW = true;

/**
 * The compact-list result card: art, with equip floating over its top-right.
 *
 * Everything the detailed card spells out — stats, conditions, the set name —
 * moves to the hover panel and to the overlay a tap opens, so the tile carries
 * only what you recognise an item by. Shared by ItemCard and SetCard so both
 * densities keep one grid cell shape.
 */
const CompactCardTile = React.forwardRef(({
    info,
    equipLabel,
    equipIcon,
    onEquip,
    equipClassName = '',
    badge = null,
    art,
    name,
    gradient,
    gradientHover,
    iconFilter,
    className = '',
    onClick,
    onPointerEnter,
    onPointerLeave,
}, ref) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleKeyDown = (event) => {
        if (!onClick) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onClick(event);
    };

    return (
        <div
            ref={ref}
            className={`compact-card card panel${className ? ` ${className}` : ''}`}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            // The art carries the name in its alt text, but the tile itself is
            // what gets focused, and with the name hidden there is nothing in it
            // for a screen reader to read out.
            aria-label={name}
            onClick={onClick}
            onKeyDown={onClick ? handleKeyDown : undefined}
            onMouseEnter={(e) => {
                setIsHovered(true);
                if (onPointerEnter) onPointerEnter(e);
            }}
            onMouseLeave={(e) => {
                setIsHovered(false);
                if (onPointerLeave) onPointerLeave(e);
            }}
        >
            <div
                className="compact-card-gradient"
                style={{
                    background: isHovered ? (gradientHover || gradient) : gradient,
                    '--item-element-filter-icon': SHOW_ELEMENT_GLOW ? iconFilter : undefined,
                }}
            >
                <CardTopbar
                    info={SHOW_TOPBAR_INFO ? info : null}
                    equipLabel={equipLabel}
                    equipIcon={equipIcon}
                    onEquip={onEquip}
                    equipClassName={equipClassName}
                    badge={badge}
                />
                <div className="compact-card-art">{art}</div>
                {SHOW_NAME && <span className="compact-card-name">{name}</span>}
            </div>
        </div>
    );
});

export default CompactCardTile;
